/* eslint-disable @typescript-eslint/naming-convention */
import { Component, computed, input } from '@angular/core';
import { NgpButton } from 'ng-primitives/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { AngularSvgIconModule } from 'angular-svg-icon';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center',
    'font-dm text-white text-13 2xl:text-sm',
    'whitespace-nowrap rounded',
    'transition-all cursor-pointer',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
    'disabled:cursor-not-allowed',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'
  ].join(' '),
  {
    variants: {
      size: {
        default: 'h-11 px-4 py-2 !text-13',
        md: 'h-11 px-4 py-2 rounded-lg !text-13',
        sm: 'h-8 px-3 rounded-lg !text-xs',
        lg: 'h-14 px-8 !text-13',
        icon: '!p-0 !size-8 rounded-full *:shrink-0 flex items-center justify-center'
      },
      variant: {
        primary: 'bg-primary hover:bg-primary/80 disabled:bg-primary/70',
        destructive: 'bg-danger hover:bg-danger disabled:bg-danger/70',
        outline: [
          'border border-primary bg-transparent',
          '!text-primary-500',
          'hover:bg-primary-500 hover:!text-white',
          'disabled:opacity-50 disabled:pointer-events-none',
          'aria-busy:text-red-800'
        ].join(' '),
        secondary:
          'bg-secondary-500 hover:bg-secondary-600 disabled:bg-secondary-400',
        secondaryOutline: [
          'border border-secondary-500 bg-transparent',
          '!text-secondary-500',
          'hover:bg-secondary-500 hover:!text-white',
          'disabled:opacity-50 disabled:pointer-events-none',
          'aria-busy:text-red-800'
        ].join(' '),
        ghost: 'bg-neutral-100 hover:bg-neutral-300/90 hover:text-accent',
        primaryLight:
          'bg-primary-50 hover:bg-primary-50 !text-primary-500 disabled:opacity-70',
        secondaryLight:
          'bg-secondary-100 text-secondary-500 hover:bg-secondary-200 disabled:opacity-70',
        info: 'bg-info-500 disabled:bg-info-400'
      },
      fullWidth: {
        true: 'w-full',
        false: ''
      },
      loading: {
        true: 'cursor-not-allowed',
        false: ''
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
      fullWidth: false,
      loading: false
    }
  }
);
export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Component({
  selector: '[button], [app-button]',
  template: `
    <span class="flex items-center gap-1.5 [font-size:inherit] font-[inherit]">
      @if (loading()) {
        <span class="animate-spin">
          <svg-icon
            src="/assets/icons/loader.svg"
            [svgStyle]="{
              width: '24px',
              height: '24px',
              fill: 'currentColor'
            }"
          ></svg-icon>
        </span>

        @if (loadingText()) {
          <span class="[font-size:inherit] font-[inherit]">
            {{ loadingText() }}
          </span>
        }
      }

      @if (!loading()) {
        <ng-content />
      }
    </span>
  `,
  hostDirectives: [{ directive: NgpButton, inputs: ['disabled'] }],
  host: {
    '[class]': 'computedClasses()',
    '[attr.disabled]': 'isDisabled() || null',
    '[attr.aria-busy]': 'loading() || null'
  },
  imports: [AngularSvgIconModule]
})
export class ButtonComponent {
  size = input<ButtonVariants['size']>();
  type = input<string>('button');
  variant = input<ButtonVariants['variant']>('primary');
  fullWidth = input<boolean>(false);
  disabled = input<boolean>(false);
  loading = input<boolean, boolean | string>(false, {
    transform: value => (typeof value === 'string' ? value === 'true' : value)
  });

  loadingText = input<string>('');

  computedClasses = computed(() =>
    buttonVariants({
      variant: this.variant(),
      size: this.size(),
      fullWidth: this.fullWidth(),
      loading: this.loading()
    })
  );

  isDisabled = computed(() => this.disabled() || this.loading());
}
